import { ErpSpan, ErpA } from './ErpHtml';
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { iconFor } from '@/lib/iconRegistry';
import { LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { translateDisplayLabel } from '@/utils/translateDisplayLabel';

export interface ErpButtonProps {
  variant?: string;
  size?: 'sm' | 'md' | 'lg' | string;
  block?: boolean;
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
  rounded?: boolean;
  ripple?: boolean;
  to?: string | object;
  href?: string;
  type?: 'button' | 'submit' | 'reset' | string;
  pill?: boolean;
  squared?: boolean;
  className?: string;
  title?: string;
  ariaLabel?: string;
  onClick?: (e: any) => void;
  children?: React.ReactNode;
  [key: string]: any;
}

const ErpButton = React.forwardRef<HTMLButtonElement, ErpButtonProps>(({
  variant = 'primary',
  size = 'md',
  block,
  disabled,
  loading,
  icon,
  rounded: _rounded,
  ripple: _ripple,
  to,
  href,
  type = 'button',
  pill: _pill,
  squared,
  className = '',
  title,
  ariaLabel,
  onClick,
  children,
  ...props
}, ref) => {
  const { t } = useTranslation();
  const localizedChildren = typeof children === 'string' ? translateDisplayLabel(t, children) : children;

  // Map legacy variants to Shadcn variants
  let mappedVariant: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" = "default";
  let variantClass = "";
  const v = variant.toLowerCase();

  if (v.includes('danger') || v.includes('error')) {
    mappedVariant = "destructive";
  } else if (v.includes('success')) {
    // No shadcn 'success' variant — compose the green CTA look (POS "Pay
    // Now" etc. used to silently fall back to primary).
    mappedVariant = "default";
    variantClass = "bg-emerald-700 hover:bg-emerald-800 text-white";
  } else if (v.includes('warning')) {
    mappedVariant = "default";
    variantClass = "bg-amber-500 hover:bg-amber-600 text-amber-950";
  } else if (v.includes('outline')) {
    mappedVariant = "outline";
  } else if (v.includes('secondary') || v.includes('light') || v.includes('info') || v.includes('subtle')) {
    mappedVariant = "secondary";
  } else if (v.includes('transparent') || v.includes('ghost')) {
    mappedVariant = "ghost";
  } else if (v.includes('link')) {
    mappedVariant = "link";
  }

  // Map sizes
  const sizes: Record<string, NonNullable<React.ComponentProps<typeof Button>['size']>> = {
    md: 'default', default: 'default', xs: 'xs', sm: 'sm', lg: 'lg',
    icon: 'icon', 'icon-xs': 'icon-xs', 'icon-sm': 'icon-sm', 'icon-lg': 'icon-lg',
  };
  let mappedSize = sizes[size] ?? 'default';
  if (!children && icon && !size.startsWith('icon')) mappedSize = 'icon';

  const propAriaLabel = props['aria-label'];

  React.useEffect(() => {
    if (mappedSize === "icon" && !ariaLabel && !title && !propAriaLabel && import.meta.env.DEV) {
      console.warn('a11y: ErpButton rendered as icon-only button without an aria-label or title', { icon, className });
    }
  }, [mappedSize, ariaLabel, title, propAriaLabel, icon, className]);

  const a11yProps: Record<string, string> = {};
  if (ariaLabel) {
    a11yProps['aria-label'] = ariaLabel;
  }
  if (title) {
    a11yProps['title'] = title;
  }

  // squared overrides the default rounded-full from button.tsx
  const classNames = [
    block ? 'w-full' : '',
    squared ? 'rounded-none' : '',
    variantClass,
    className
  ].filter(Boolean).join(' ');

  const content = (
    <>
      {loading && <LoaderCircle size={16} className="animate-spin" />}
      {!loading && icon && (
        <ErpSpan className="inline-flex shrink-0 items-center">
          {iconFor(icon, 16)}
        </ErpSpan>
      )}
      {localizedChildren}
    </>
  );

  const handleClick = (e: any) => {
    if (disabled || loading) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (onClick) onClick(e);
  };

  // `disabled` is not a valid attribute on <a>/Link — expose aria-disabled
  // instead (handleClick still blocks click/Enter activation).
  const isDisabled = !!(disabled || loading);
  const linkClassName = [classNames, isDisabled ? 'opacity-50' : ''].filter(Boolean).join(' ');

  if (to) {
    return (
      <Button variant={mappedVariant} size={mappedSize} className={linkClassName} aria-disabled={isDisabled || undefined} asChild ref={ref} {...a11yProps} {...props}>
        <Link to={to as any} onClick={handleClick}>{content}</Link>
      </Button>
    );
  }

  if (href) {
    return (
      <Button variant={mappedVariant} size={mappedSize} className={linkClassName} aria-disabled={isDisabled || undefined} asChild ref={ref} {...a11yProps} {...props}>
        <ErpA href={href} onClick={handleClick}>{content}</ErpA>
      </Button>
    );
  }

  return (
    <Button
      variant={mappedVariant}
      size={mappedSize}
      className={classNames}
      type={type as any}
      disabled={disabled || loading}
      onClick={handleClick}
      ref={ref}
      {...a11yProps}
      {...props}
    >
      {content}
    </Button>
  );
});

export default React.memo(ErpButton);
